import { NotificationsActive, TimeToLeaveRounded } from '@mui/icons-material';
import { Box, CircularProgress, Divider, Tooltip, Typography, useTheme } from '@mui/material';
import { useAppDispatch } from 'app/config/store';
import { TruckImplementListDTO } from 'app/shared/model/truck-implement.model';
import React, { useEffect, useState } from 'react';
import { ITruckGroupAlertDetails, getGroupAlertDetails, getGroupTruckDetails } from '../truck-group.reducer';
import { TruckGroupFenceSyncPanel } from './TruckGroupFenceSyncPanel';
import { translate } from 'react-jhipster';

export type TruckGroupDetailView = 'trucks' | 'alerts' | 'fences';

interface Props {
  groupId: number;
  activeView: TruckGroupDetailView;
}

const formatAlertValue = (alert: ITruckGroupAlertDetails) => {
  if (alert.valueInRangeMin != null || alert.valueInRangeMax != null) {
    return [alert.valueInRangeMin, alert.valueInRangeMax].filter(value => value != null).join(' - ');
  }

  return alert.value != null ? `${alert.value}` : '';
};

const formatAlertDetails = (alert: ITruckGroupAlertDetails) => {
  const alertValue = formatAlertValue(alert);
  return [
    alert.alertType ? `Tipo: ${translate(`fleetSenseApp.AlertType.${alert.alertType}`)}` : null,
    alert.sensorName ? `Sensor: ${alert.sensorName}` : null,
    alertValue ? `Valor: ${alertValue}` : null,
  ]
    .filter(Boolean)
    .join(' | ');
};

export const TruckGroupDetailsPanel = ({ groupId, activeView }: Props) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const [trucks, setTrucks] = useState<TruckImplementListDTO[] | null>(null);
  const [alerts, setAlerts] = useState<ITruckGroupAlertDetails[] | null>(null);
  const [loadingView, setLoadingView] = useState<TruckGroupDetailView | null>(null);

  useEffect(() => {
    if (activeView !== 'trucks' || trucks !== null) {
      return;
    }

    setLoadingView('trucks');
    dispatch(getGroupTruckDetails(groupId)).then(result => {
      if (getGroupTruckDetails.fulfilled.match(result)) {
        setTrucks(result.payload.data);
      }
      setLoadingView(null);
    });
  }, [activeView, groupId, trucks]);

  useEffect(() => {
    if (activeView !== 'alerts' || alerts !== null) {
      return;
    }

    setLoadingView('alerts');
    dispatch(getGroupAlertDetails(groupId)).then(result => {
      if (getGroupAlertDetails.fulfilled.match(result)) {
        setAlerts(result.payload.data);
      }
      setLoadingView(null);
    });
  }, [activeView, groupId, alerts]);

  if (loadingView === activeView) {
    return (
      <Box display="flex" justifyContent="center" py={2.5}>
        <CircularProgress size={20} color="secondary" />
      </Box>
    );
  }

  if (activeView === 'trucks') {
    if (!trucks || trucks.length === 0) {
      return (
        <Box px={2.5} py={2}>
          <Typography variant="caption" color="text.secondary">
            Nenhum ativo vinculado a este grupo.
          </Typography>
        </Box>
      );
    }

    return (
      <Box px={2.5} py={1} display="flex" flexDirection="column">
        {trucks.map((truck, index) => (
          <Box key={truck.id}>
            {index > 0 && <Divider sx={{ opacity: 0.08 }} />}
            <Box display="flex" alignItems="center" gap={1.25} py={1} minWidth={0}>
              <TimeToLeaveRounded sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
              <Box minWidth={0} flex={1} overflow="hidden">
                <Tooltip title={truck.licensePlate || `Ativo #${truck.id}`}>
                  <Typography variant="body2" color="text.primary" fontWeight={600} noWrap sx={{ display: 'block' }}>
                    {truck.licensePlate || `Ativo #${truck.id}`}
                  </Typography>
                </Tooltip>
                {truck.truckGroupName && (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {truck.truckGroupName}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (activeView === 'alerts') {
    if (!alerts || alerts.length === 0) {
      return (
        <Box px={2.5} py={2}>
          <Typography variant="caption" color="text.secondary">
            Nenhum alerta vinculado a este grupo.
          </Typography>
        </Box>
      );
    }

    return (
      <Box px={2.5} py={1} display="flex" flexDirection="column">
        {alerts.map((alert, index) => {
          const alertDetails = formatAlertDetails(alert);
          return (
            <Box key={alert.id}>
              {index > 0 && <Divider sx={{ opacity: 0.08 }} />}
              <Box display="flex" alignItems="center" gap={1.25} py={1} minWidth={0}>
                <NotificationsActive sx={{ fontSize: 18, color: theme.palette.warning.main, flexShrink: 0 }} />
                <Box minWidth={0} flex={1} overflow="hidden">
                  <Tooltip title={alert.name || `Alerta #${alert.id}`}>
                    <Typography variant="body2" color="text.primary" fontWeight={600} noWrap sx={{ display: 'block' }}>
                      {alert.name || `Alerta #${alert.id}`}
                    </Typography>
                  </Tooltip>
                  {alertDetails && (
                    <Tooltip title={alertDetails}>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                        {alertDetails}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }

  return <TruckGroupFenceSyncPanel groupId={groupId} />;
};
